import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { getAllTools, type ToolPageType } from '@/data/tools';
import NavBar from '@/components/NavBar';
import SEOHead from '@/components/SEOHead';
import BreadcrumbSEO from '@/components/BreadcrumbSEO';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useState } from 'react';

const categoryLabels: Record<ToolPageType, string> = {
  compression: 'Compresión',
  conversion: 'Conversión',
  'target-size': 'Tamaño objetivo',
  resize: 'Redimensionar',
  optimization: 'Optimización',
};

const categoryOrder: ToolPageType[] = ['compression', 'target-size', 'optimization', 'conversion', 'resize'];

const CategorySection = ({ type, label }: { type: ToolPageType; label: string }) => {
  const tools = getAllTools().filter(t => t.pageType === type);
  const [open, setOpen] = useState(true);

  if (tools.length === 0) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mb-8">
      <CollapsibleTrigger className="flex items-center gap-2 w-full text-left mb-4 group cursor-pointer">
        <h2 className="text-xl font-bold text-foreground">{label}</h2>
        <Badge variant="secondary" className="text-xs">{tools.length}</Badge>
        <ChevronDown className={`h-4 w-4 text-muted-foreground ml-auto transition-transform ${open ? 'rotate-180' : ''}`} />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tools.map(tool => (
            <Link
              key={tool.slug}
              to={`/herramientas/${tool.slug}`}
              className="group block p-5 rounded-xl border border-border bg-card hover:shadow-md hover:border-primary/30 transition-all duration-200"
            >
              <h3 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors mb-1.5">
                {tool.title}
              </h3>
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                {tool.subtitle}
              </p>
              <span className="inline-flex items-center text-sm text-primary font-medium">
                Usar herramienta
                <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

const Herramientas = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Herramientas de Imagen Online Gratis | CompresorJPEG"
        description="Explora todas nuestras herramientas gratuitas para comprimir, convertir, redimensionar y optimizar imágenes online. JPG, PNG, WebP y más."
        canonical="https://compresorjpeg.com/herramientas"
        keywords="herramientas imagen online, comprimir imagen, convertir imagen, redimensionar imagen, optimizar imagen"
      />

      <NavBar />

      <main className="container mx-auto px-4 py-6">
        <BreadcrumbSEO
          items={[
            { label: 'Inicio', href: '/' },
            { label: 'Herramientas' },
          ]}
        />

        <section className="text-center py-10 max-w-3xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Herramientas de imagen online
          </h1>
          <p className="text-lg text-muted-foreground">
            Comprime, convierte, redimensiona y optimiza tus imágenes con nuestras herramientas gratuitas.
          </p>
        </section>

        <div className="max-w-5xl mx-auto pb-16">
          {categoryOrder.map(type => (
            <CategorySection key={type} type={type} label={categoryLabels[type]} />
          ))}
        </div>

        {/* SEO Content Section */}
        <section className="max-w-3xl mx-auto pb-16 prose prose-gray">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Herramientas para optimizar imágenes online
          </h2>
          <p className="text-muted-foreground mb-4">
            En CompresorJPEG ofrecemos un conjunto completo de herramientas gratuitas para trabajar con imágenes. 
            Puedes{' '}
            <Link to="/herramientas/comprimir-jpg" className="text-primary hover:underline">
              comprimir imágenes JPG
            </Link>{' '}
            para reducir su peso sin perder calidad visual, optimizar archivos PNG y WebP para tu sitio web, 
            o convertir entre formatos según tus necesidades.
          </p>
          <p className="text-muted-foreground mb-4">
            Si necesitas enviar fotos por email, subirlas a formularios con límites de tamaño o simplemente 
            ahorrar espacio de almacenamiento, nuestra herramienta para{' '}
            <Link to="/herramientas/reducir-imagen-200kb" className="text-primary hover:underline">
              reducir imágenes a un tamaño objetivo
            </Link>{' '}
            te permite ajustar el peso del archivo de forma precisa. También puedes redimensionar imágenes 
            para adaptarlas a las medidas exactas que necesitas.
          </p>
          <p className="text-muted-foreground">
            Para quienes buscan mejorar el rendimiento de su sitio web, la{' '}
            <Link to="/herramientas/optimizar-imagen-online" className="text-primary hover:underline">
              optimización de imágenes
            </Link>{' '}
            es esencial: imágenes más ligeras significan tiempos de carga más rápidos, mejores Core Web Vitals 
            y una experiencia de usuario superior. Todas nuestras herramientas funcionan directamente en el 
            navegador, sin necesidad de instalar software ni crear una cuenta.
          </p>
        </section>
      </main>

      <footer className="border-t border-border py-8">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} CompresorJPEG — Todas las herramientas de optimización de imágenes online.</p>
        </div>
      </footer>
    </div>
  );
};

export default Herramientas;
